import { c as _c } from "react/compiler-runtime";
import { useCarePlanContext } from "../care-plan";
import { useCarePlanPrint } from "../print/care-plan-print-context";
import { CarePlanBranch, hasTimelineStepAfterDecision } from "../types";
import { ReactNode, createContext, useContext, useMemo } from "react";
import { cx } from "@/lib/tailwind-utils";
import { useDecisionStep } from "./decision-step-context";
interface BranchContextValue {
  branch: CarePlanBranch;
  isSelected: boolean;
}
const BranchContext = createContext<BranchContextValue | null>(null);
export function useBranch() {
  const ctx = useContext(BranchContext);
  if (!ctx) {
    throw new Error("useBranch must be used within a DecisionStep.Branch");
  }
  return ctx;
}

/** Returns the branch context if inside a Branch, or null otherwise. */
export function useOptionalBranch() {
  return useContext(BranchContext);
}
interface BranchProps {
  branch: CarePlanBranch;
  children: ReactNode;
}
export function Branch(t0) {
  const $ = _c(48);
  const {
    branch,
    children
  } = t0;
  const {
    state: t1
  } = useDecisionStep();
  const {
    selectedBranchId,
    branches,
    decisionStep
  } = t1;
  const {
    state: t2,
    config: t3
  } = useCarePlanContext();
  const {
    steps
  } = t2;
  const {
    orientation,
    readOnly
  } = t3;
  const isFirstBranch = branches[0]?.id === branch.id;
  const isLastBranch = branches[branches.length - 1]?.id === branch.id;
  let t4;
  if ($[0] !== decisionStep.id || $[1] !== steps) {
    t4 = hasTimelineStepAfterDecision(steps, decisionStep.id);
    $[0] = decisionStep.id;
    $[1] = steps;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const hasStepsAfterDecision = t4;
  const printing = useCarePlanPrint();
  const isSelected = selectedBranchId === branch.id;
  let t5;
  if ($[3] !== branch || $[4] !== isSelected) {
    t5 = {
      branch,
      isSelected
    };
    $[3] = branch;
    $[4] = isSelected;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const value = t5;
  if (!printing && !isSelected && !!selectedBranchId) {
    return null;
  }
  if (orientation === "horizontal") {
    let t6;
    if ($[6] !== isFirstBranch || $[7] !== isLastBranch || $[8] !== isSelected || $[9] !== printing) {
      t6 = (printing || !isSelected) && <div className={cx("w-4 border-l shrink-0", isFirstBranch ? "h-1/2 self-end border-t rounded-tl-xl" : isLastBranch ? "h-1/2 rounded-bl-xl border-b" : "")} />;
      $[6] = isFirstBranch;
      $[7] = isLastBranch;
      $[8] = isSelected;
      $[9] = printing;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    let t7;
    if ($[11] !== children) {
      t7 = <div className="flex items-center gap-2 py-1">{children}</div>;
      $[11] = children;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    let t8;
    if ($[13] !== hasStepsAfterDecision || $[14] !== isFirstBranch || $[15] !== isLastBranch || $[16] !== printing || $[17] !== readOnly || $[18] !== selectedBranchId) {
      t8 = (hasStepsAfterDecision || !readOnly) && (printing || !selectedBranchId) && <div className={cx("w-8 border-r shrink-0 grow", isFirstBranch ? "h-1/2 self-end border-t rounded-tr-xl" : isLastBranch ? "h-1/2 rounded-br-xl border-b" : "")} />;
      $[13] = hasStepsAfterDecision;
      $[14] = isFirstBranch;
      $[15] = isLastBranch;
      $[16] = printing;
      $[17] = readOnly;
      $[18] = selectedBranchId;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    let t9;
    if ($[20] !== t6 || $[21] !== t7 || $[22] !== t8) {
      t9 = <div className="flex items-stretch">{t6}{t7}{t8}</div>;
      $[20] = t6;
      $[21] = t7;
      $[22] = t8;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    let t10;
    if ($[24] !== t9 || $[25] !== value) {
      t10 = <BranchContext.Provider value={value}>{t9}</BranchContext.Provider>;
      $[24] = t9;
      $[25] = value;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    return t10;
  }
  let t6;
  if ($[27] !== isFirstBranch || $[28] !== isLastBranch || $[29] !== isSelected || $[30] !== printing) {
    t6 = (printing || !isSelected) && <div className={cx("h-4 border-t shrink-0", isFirstBranch ? "w-1/2 self-end border-l rounded-tl-xl" : isLastBranch ? "w-1/2 border-r rounded-tr-xl" : "")} />;
    $[27] = isFirstBranch;
    $[28] = isLastBranch;
    $[29] = isSelected;
    $[30] = printing;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  let t7;
  if ($[32] !== children) {
    t7 = <div className="flex flex-col items-center gap-2 px-1">{children}</div>;
    $[32] = children;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  let t8;
  if ($[34] !== hasStepsAfterDecision || $[35] !== isFirstBranch || $[36] !== isLastBranch || $[37] !== printing || $[38] !== readOnly || $[39] !== selectedBranchId) {
    t8 = (hasStepsAfterDecision || !readOnly) && (printing || !selectedBranchId) && <div className={cx("h-8 border-b shrink-0 grow", isFirstBranch ? "w-1/2 self-end border-l rounded-bl-xl" : isLastBranch ? "w-1/2 border-r rounded-br-xl" : "")} />;
    $[34] = hasStepsAfterDecision;
    $[35] = isFirstBranch;
    $[36] = isLastBranch;
    $[37] = printing;
    $[38] = readOnly;
    $[39] = selectedBranchId;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  let t9;
  if ($[41] !== t6 || $[42] !== t7 || $[43] !== t8) {
    t9 = <div className="flex min-w-80 flex-col items-stretch">{t6}{t7}{t8}</div>;
    $[41] = t6;
    $[42] = t7;
    $[43] = t8;
    $[44] = t9;
  } else {
    t9 = $[44];
  }
  let t10;
  if ($[45] !== t9 || $[46] !== value) {
    t10 = <BranchContext.Provider value={value}>{t9}</BranchContext.Provider>;
    $[45] = t9;
    $[46] = value;
    $[47] = t10;
  } else {
    t10 = $[47];
  }
  return t10;
}